import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Col,
  Form,
  Upload,
  Button,
  Tooltip,
  message,
  Typography,
} from 'antd';

import {
  UploadOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  FileUnknownOutlined,
} from '@ant-design/icons';

import Previewer from './Previewer';

const { Text } = Typography;

const Uploader = ({
  title,
  limitMB,
  fullSize,
  formItemName,
  allowedExtensions,
  allowPreview,
  rules,
  // clearFiles,
  ...rest
}) => {
  const form = Form.useFormInstance();
  const file = Form.useWatch(formItemName, form);
  const defaultBreakPoints = { xs: 24, sm: 24, md: 8 };
  const [fileList, setFileList] = useState([]);
  const [visible, setVisible] = useState(false);
  const iFrameUrl = typeof file === 'string' ? file : null;

  const getProps = () => (fullSize ? { span: 24 } : defaultBreakPoints);
  const accept = allowedExtensions.map((e) => `.${e}`).join(',');
  const formats = allowedExtensions.join(', ').toUpperCase();

  useEffect(() => {
    if (!file) setFileList([]);
    // eslint-disable-next-line
  }, [file]);

  return (
    <>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <Col {...getProps()}>
        <Form.Item
          name={formItemName}
          label={(
            <>
              <Text style={{ marginRight: 5 }}>{title}</Text>
              {(allowPreview && iFrameUrl) && (
                <Tooltip title="Click para ver preview">
                  <Button
                    type="link"
                    onClick={() => setVisible(true)}
                    icon={<EyeOutlined />}
                  />
                </Tooltip>
              )}
              {(!allowPreview && iFrameUrl) && (
                <Tooltip
                  title="Por motivos de seguridad este archivo
                    no es accesible, si desea modificarlo suba un archivo"
                >
                  <CheckCircleOutlined style={{ marginLeft: 10, color: '#1bc943' }} />
                </Tooltip>
              )}
              {!!accept && (
                <Tooltip
                  title={`Solo se admiten archivos en formato ${formats} de hasta ${limitMB} MB`}
                >
                  <FileUnknownOutlined style={{ marginLeft: 10 }} />
                </Tooltip>
              )}
            </>
          )}
          rules={rules}
          valuePropName={rest.valuePropName}
          className={rest.formItemClassName}
          hidden={rest.formItemHidden}
        >
          <Upload
            beforeUpload={() => false}
            onRemove={() => {
              setFileList([]);
              form.resetFields([formItemName]);
            }}
            onChange={(e) => {
              const { name } = e.file;
              if (e.file.status !== 'removed') {
                const isValidExt = !allowedExtensions.length || allowedExtensions
                  .includes(name.split('.').pop().toLocaleLowerCase());

                if (!isValidExt) {
                  message.warn(`Solo se admiten archivos en formato ${formats}`);
                }
                const isLmt = e.file.size / 1024 / 1024 < limitMB;
                if (!isLmt) {
                  message.warn(`El archivo no debe sobrepasar los ${limitMB}MB`);
                }
                if (isValidExt && isLmt) {
                  setFileList([e.file]);
                }
              } else {
                setFileList([]);
                form.resetFields([formItemName]);
              }
            }}
            fileList={fileList}
            accept={accept}
            disabled={!!rest.disabled}
          >
            <Button disabled={!!rest.disabled}>
              <UploadOutlined />
              {' '}
              Subir archivo
            </Button>
          </Upload>
        </Form.Item>
      </Col>
      {allowPreview && (
        <Previewer
          visible={visible}
          setVisible={setVisible}
          iFrameUrl={iFrameUrl}
        />
      )}
    </>
  );
};

Uploader.propTypes = {
  // clearFiles: PropTypes.bool,
  allowPreview: PropTypes.bool,
  fullSize: PropTypes.bool,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  limitMB: PropTypes.number,
  formItemName: PropTypes.string.isRequired,
  allowedExtensions: PropTypes.arrayOf(PropTypes.string),
  rules: PropTypes.arrayOf(PropTypes.shape({})),
};

Uploader.defaultProps = {
  // clearFiles: false,
  fullSize: false,
  allowPreview: true,
  rules: [],
  allowedExtensions: [],
  limitMB: 5,
};

export default Uploader;
